import { HOW_IT_WORKS_ART_XML, HOW_IT_WORKS_CARDS } from '../../../shared/howItWorksArt.js'
import { smart } from '../lib/typography'
import { WORKER_SECTION_CARD_XML } from '../../../shared/workerSectionArt.js'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import Hero from '../components/ds/Hero'
import StoreBadges from '../components/ds/StoreBadges'
import Section from '../components/ds/Section'
import SectionHead, { H2 } from '../components/ds/SectionHead'
import CtaBand from '../components/ds/CtaBand'
import BankStrip from '../components/ds/BankStrip'
import Button from '../components/ds/Button'
import Art from '../components/ds/Art'
import FaqAccordion from '../components/ds/FaqAccordion'
import Proof from '../components/ds/Proof'
import ProductTour from '../components/ds/ProductTour'
import { AppScreen } from '../components/ds/PhoneFrame'
import { HERO_WORK } from '../content/heroMedia'
import { WORKER_QUOTES } from '../content/quotes'
import { findWorkJsonLd } from '../lib/landingJsonLd'

// The Find work trunk home. The hard side of the marketplace, and the page most
// organic traffic lands on, so it is `/`.
//
// Copy rules it has to keep (CLAUDE.md, and §9 of the spec): outcomes not
// mechanics, no dashes, UK spelling, no vendor names, never the words practice,
// clinic, employer, payroll, agency or temp. Say what happens, then the one action.
//
// Section 5 is deliberately the app’s OWN four cards, art and words, read straight
// from shared/howItWorksArt.js. A visitor sees here exactly what they will meet in
// the app, and the two can never drift because there is one copy.

const MONEY = [
  {
    art: WORKER_SECTION_CARD_XML.work,
    title: 'Know your pay up front',
    body: "Every shift shows exactly what it pays before you say yes. No haggling with an agency, no surprises when the money lands.",
  },
  {
    art: WORKER_SECTION_CARD_XML.gettingPaid,
    title: 'Clock out, get paid',
    // ⚠️ The asterisk is load bearing (CAP code): the claim is bold up here and
    // qualified by MONEY_FOOTNOTE below the cards. Instant approval pays most
    // shifts at clock out; overtime, a disputed timesheet or one the workplace
    // must confirm can take up to 24 hours. Keep the two in step.
    body: "Clock out and your earnings land straight in your wallet in seconds.* No waiting for weekly pay runs. Withdraw whenever you’re ready.",
  },
  {
    art: WORKER_SECTION_CARD_XML.account,
    title: 'Your money, your timing',
    body: 'Move it to your bank whenever you like. An instant transfer lands in minutes.*',
  },
]

// The operational detail behind the asterisks above, kept out of the headline
// copy but on the same screen (an ASA qualification must sit near the claim and
// clarify it, never contradict it).
const MONEY_FOOTNOTE =
  '* Most shifts pay the moment you clock out. Overtime, a timesheet under review or a payment that needs checking can take up to 24 hours to clear. Instant transfers can occasionally take longer, depending on your bank.'

const CHOICES = [
  { title: 'Only shifts you can take', body: "Every shift in your feed is one you can book today. No dead ends, no wasted calls." },
  { title: 'Your rate, your call',     body: "Every rate is upfront. Just tap the shifts that suit you and ignore the rest. No awkward rejections or explanations needed." },
  { title: 'Near you, on your terms',  body: "Choose how far you’ll travel and the days you’re free. We stay quiet the rest of the time." },
  { title: 'Places you like',          body: "Favourite the workplaces you love working at. Their shifts reach you first." },
]

// Every one of these is live in the worker app: profile, workplace reviews
// (app/review-workplace, published without name), the reliability score
// (app/booked), followers, groups and messages.
const COMMUNITY = [
  { title: "A profile that stands out", body: "Your verified credentials, skills, and work history in one place. Workplaces see a trusted professional, not another CV buried in a pile." },
  { title: "Reviews that go both ways", body: "Rate the places you work anonymously, and check honest feedback from other professionals before accepting a shift." },
  // ⛔ Real since the reliability head start (servers lib/headStart.js). That ships
  // behind HEAD_START_ENABLED, so do not deploy this copy until the switch is on.
  { title: "A reliability score that works for you", body: "Turn up, do great work, and watch your score climb. A higher score gives you a head start with priority views on upcoming shifts before anyone else sees them." },
  { title: "Your people", body: "Follow colleagues, join groups, and message peers. A real community of dental professionals, built for the way you work." },
]

const FAQS = [
  {
    q: 'When do I actually get paid?',
    a: 'The moment you clock out. Your pay lands straight in your TempDash wallet, and you move it to your bank whenever you want. Overtime, a timesheet under review or a payment that needs checking can take up to 24 hours to clear.',
  },
  {
    q: 'How fast can I get money into my bank?',
    a: 'Your pay lands in your TempDash wallet the moment you clock out, and an instant transfer puts it in your bank in minutes. Overtime, a timesheet under review or a payment that needs checking can take up to 24 hours to clear.',
  },
  {
    q: 'What does it cost me?',
    a: 'Nothing to join and nothing to accept a shift. The rate you see is the rate you are paid for every hour you work.',
  },
  {
    q: 'Am I employed by TempDash?',
    a: "No. You’re self employed, you choose every shift you take, and you’re never obliged to take one.",
  },
  {
    q: 'What do I need before I can work?',
    a: 'Proof of who you are, your right to work in the UK, your professional registration, and the documents your role needs. Setting it up takes about ten minutes and you only do it once.',
  },
  {
    q: 'What if something goes wrong on the day?',
    a: 'Tell us on the shift and we will get you to the right person. You are not left to sort it out with the workplace on your own.',
  },
  {
    q: 'Where does TempDash work?',
    a: 'We are opening in London first and adding areas as we go. Join and we will tell you the day we reach yours.',
  },
]

export default function FindWork() {
  return (
    <div className="bg-canvas">
      <Seo
        fullTitle="Locum shifts near you, paid the day you work · TempDash"
        description="Dental locum shifts near you, matched to your verified skills. See the rate before you accept, get paid the day you work, and cash out in minutes."
        path="/"
        jsonLd={findWorkJsonLd(FAQS)}
      />
      <MarketingNav />

      {/* ⚠️ The ONE main landmark. Without it every <section> on the page
          sits outside any landmark and a screen reader user navigating by
          landmark cannot reach the content at all. The skip link in
          MarketingNav targets this id. */}
      <main id="main" tabIndex={-1} className="outline-none">

      <Hero
        trust={<StoreBadges audience="work" source="hero" tone="light" />}
        media={HERO_WORK}
        title={<>Work when you want.<br />Get paid when you’re done.</>}
        lead="Dental locum shifts near you, matched to your skills. See exactly what each one pays before you say yes, and watch the money land the same day. No agency. No invoices. No waiting for a pay run."
      >
        <Button href="/signup?for=work" variant="light">Join TempDash</Button>
        <Button to="/how-it-works" variant="quietLight">
          See how it works
        </Button>
      </Hero>

      <Section ground="canvas">
        <SectionHead eyebrow="The money" title={<>Paid the day you work.</>} />
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {MONEY.map((m) => (
            <div key={m.title}>
              <Art xml={m.art} className="h-[72px] w-[72px]" />
              <h3 className="mt-4 text-[21px] font-semibold">{m.title}</h3>
              <p className="mt-2 text-[17px] leading-relaxed text-ink-muted">{m.body}</p>
            </div>
          ))}
        </div>
        <BankStrip className="mt-14 border-t border-hairline pt-10" />
        <p className="mt-12 max-w-[760px] text-[13px] leading-relaxed text-ink-muted/80">
          {MONEY_FOOTNOTE}
        </p>
      </Section>

      <ProductTour
        ground="navy"
        eyebrow="The app"
        title="See it. Take it. Get paid for it."
        lead="No phone calls, no paper invoices, and zero waiting around to find out what a shift actually pays."
        steps={[
          { screen: <AppScreen id="worker-shift-offer" />, title: 'Know your worth up front',
            body: "Rate, hours, distance and your estimated take home pay, all laid out before you say yes." },
          { screen: <AppScreen id="worker-clock-in" />, title: 'Turn up. Slide in.',
            body: "One slide and you’re on shift. No sign in sheets, no paperwork, just you doing what you do best." },
          { screen: <AppScreen id="worker-wallet" />, title: 'Clock out. Cash in.',
            body: "Your pay shows in your wallet as soon as you clock out. Move it to your bank whenever you like." },
        ]}
      />

      <Section ground="canvas">
        <SectionHead eyebrow="The community" title={<>More than a shift app.</>} />
        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {COMMUNITY.map((c) => (
            <div key={c.title}>
              <h3 className="text-[19px] font-semibold">{c.title}</h3>
              <p className="mt-2 text-[16px] leading-relaxed text-ink-muted">{c.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* The app’s own four cards, art and words. */}
      <Section ground="surface">
        <SectionHead eyebrow="On the day" title={<>How a shift goes.</>} />
        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {HOW_IT_WORKS_CARDS.map((c) => (
            <div key={c.title}>
              <Art xml={HOW_IT_WORKS_ART_XML[c.art]} className="h-[72px] w-[72px]" />
              <h3 className="mt-4 text-[19px] font-semibold">{smart(c.title)}</h3>
              <p className="mt-2 text-[16px] leading-relaxed text-ink-muted">{smart(c.body)}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section ground="canvas">
        <div className="md:flex md:items-start md:gap-20">
          <div className="md:w-[380px] md:shrink-0">
            <SectionHead eyebrow="Fair matching" title={<>Your skills. Your rate. Your call.</>} />
          </div>
          <div className="mt-10 grid flex-1 gap-px overflow-hidden rounded-[16px] border border-hairline bg-hairline md:mt-0">
            {CHOICES.map((c) => (
              <div key={c.title} className="bg-surface p-7">
                <h3 className="text-[19px] font-semibold">{c.title}</h3>
                <p className="mt-1.5 text-[17px] text-ink-muted">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Renders nothing until src/content/quotes.js has consented quotes. */}
      <Proof quotes={WORKER_QUOTES} title="People who already work this way." />

      <Section ground="surface" width="text">
        <H2>Questions, answered.</H2>
        <FaqAccordion items={FAQS} className="mt-10" />
      </Section>

      <CtaBand
        ground="navy"
        size="xl"
        title="Work on your terms."
        lead="Set up once, then pick and choose the shifts that fit your schedule."
      >
        <Button href="/signup?for=work" variant="light">Join TempDash</Button>
      </CtaBand>

      </main>

      <MarketingFooter />
    </div>
  )
}
